import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { useTeam } from "@/context/TeamContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, UsersThree, Eye, PencilSimple, SoccerBall,
  ClockCounterClockwise, Crown, Copy, UserPlus,
} from "@phosphor-icons/react";
import { RosterTable } from "@/components/admin/RosterTable";
import { MembersTable } from "@/components/admin/MembersTable";
import { AuditLogList } from "@/components/admin/AuditLogList";
import { PlayerDialog } from "@/components/admin/PlayerDialog";
import { CopyRosterDialog } from "@/components/admin/CopyRosterDialog";
import { GrantAccessDialog } from "@/components/admin/GrantAccessDialog";

const emptyForm = { name: "", username: "", password: "", role: "member" };
const emptyPlayerForm = { first_name: "", last_name: "", jersey_number: "", position: "", active: true };

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTeam, currentTeamId, teams, currentRole, loading: teamsLoading } = useTeam();
  const { user } = useAuth();
  const isOwner = currentRole === "owner" || currentRole === "admin";
  const adminTierOtherTeams = teams.filter((t) => t.id !== currentTeamId && (t.my_role === "owner" || t.my_role === "admin"));

  const [members, setMembers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get("tab") || "roster");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetting, setResetting] = useState(false);

  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [playerForm, setPlayerForm] = useState(emptyPlayerForm);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [playerError, setPlayerError] = useState("");
  const [playerSaving, setPlayerSaving] = useState(false);
  const [deletePlayerTarget, setDeletePlayerTarget] = useState(null);

  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceTeamId, setCopySourceTeamId] = useState("");
  const [copySourcePlayers, setCopySourcePlayers] = useState([]);
  const [copySelected, setCopySelected] = useState(new Set());
  const [copyLoading, setCopyLoading] = useState(false);
  const [copySaving, setCopySaving] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [copyConflicts, setCopyConflicts] = useState([]);
  const [copyOverrides, setCopyOverrides] = useState({});

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantQuery, setGrantQuery] = useState("");
  const [grantResults, setGrantResults] = useState([]);
  const [grantSearching, setGrantSearching] = useState(false);
  const [grantSelected, setGrantSelected] = useState(null);
  const [grantRole, setGrantRole] = useState("member");
  const [grantSaving, setGrantSaving] = useState(false);
  const [grantError, setGrantError] = useState("");

  const load = useCallback(async () => {
    if (!currentTeamId) return;
    try {
      const [m, a, pl] = await Promise.all([
        api.get(`/teams/${currentTeamId}/members`),
        api.get(`/teams/${currentTeamId}/audit-log`),
        api.get(`/teams/${currentTeamId}/players`),
      ]);
      setMembers(m.data);
      setAuditLog(a.data);
      setPlayers(pl.data);
    } finally {
      setLoading(false);
    }
  }, [currentTeamId]);

  useEffect(() => {
    if (teamsLoading) return;
    if (!isOwner) {
      navigate("/dashboard");
      return;
    }
    if (currentTeamId) {
      setLoading(true);
      load();
    }
  }, [teamsLoading, currentTeamId, load, isOwner, navigate]);

  const openAdd = () => {
    setForm(emptyForm);
    setError("");
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post(`/teams/${currentTeamId}/members`, {
        username: form.username.trim(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
      });
      toast.success("User added");
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.put(`/teams/${currentTeamId}/members/${m.user_id}`, { active: !m.active });
      toast.success(m.active ? `${m.name} deactivated` : `${m.name} reactivated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.put(`/teams/${currentTeamId}/members/${m.user_id}`, { role });
      toast.success(`${m.name} is now a ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/teams/${currentTeamId}/members/${deleteTarget.user_id}`);
      toast.success("Access revoked");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDeleteTarget(null);
    }
  };

  const openReset = (m) => {
    setResetTarget(m);
    setResetPassword("");
    setResetError("");
  };

  const confirmReset = async () => {
    if (resetPassword.length < 6) {
      setResetError("Password must be at least 6 characters.");
      return;
    }
    setResetting(true);
    setResetError("");
    try {
      await api.post(`/teams/${currentTeamId}/members/${resetTarget.user_id}/reset-password`, { new_password: resetPassword });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setResetPassword("");
    } catch (err) {
      setResetError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  const openGrant = () => {
    setGrantOpen(true);
    setGrantQuery("");
    setGrantResults([]);
    setGrantSelected(null);
    setGrantRole("member");
    setGrantError("");
  };

  useEffect(() => {
    if (!grantOpen || grantSelected) return;
    const q = grantQuery.trim();
    if (q.length < 2) {
      setGrantResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setGrantSearching(true);
      try {
        const { data } = await api.get(`/users/search`, { params: { q } });
        setGrantResults(data);
      } catch (err) {
        /* silent — search box just shows no results */
      } finally {
        setGrantSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [grantQuery, grantOpen, grantSelected]);

  const submitGrant = async () => {
    if (!grantSelected) {
      setGrantError("Search for and select a user first.");
      return;
    }
    setGrantSaving(true);
    setGrantError("");
    try {
      await api.post(`/teams/${currentTeamId}/access-grants`, { user_id: grantSelected.user_id, role: grantRole });
      toast.success(`Granted ${grantSelected.name} ${grantRole} access`);
      setGrantOpen(false);
      load();
    } catch (err) {
      setGrantError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGrantSaving(false);
    }
  };

  const openCopyRoster = () => {
    setCopyOpen(true);
    setCopySourceTeamId("");
    setCopySourcePlayers([]);
    setCopySelected(new Set());
    setCopyError("");
    setCopyConflicts([]);
    setCopyOverrides({});
  };

  const loadSourcePlayers = async (teamId) => {
    setCopySourceTeamId(teamId);
    setCopySourcePlayers([]);
    setCopySelected(new Set());
    setCopyConflicts([]);
    setCopyOverrides({});
    setCopyError("");
    if (!teamId) return;
    setCopyLoading(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/players`);
      setCopySourcePlayers(data);
    } catch (err) {
      setCopyError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCopyLoading(false);
    }
  };

  const toggleCopySelect = (id) => {
    setCopySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllCopy = () => {
    setCopySelected((prev) => (prev.size === copySourcePlayers.length ? new Set() : new Set(copySourcePlayers.map((p) => p.id))));
  };

  const submitCopyRoster = async () => {
    if (copySelected.size === 0) {
      setCopyError("Select at least one player to copy.");
      return;
    }
    setCopySaving(true);
    setCopyError("");
    try {
      await api.post(`/teams/${currentTeamId}/copy-roster`, {
        source_team_id: copySourceTeamId,
        player_ids: Array.from(copySelected),
        overrides: copyOverrides,
      });
      toast.success("Roster copied");
      setCopyOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.conflicts) {
        const taken = new Set(players.map((p) => p.jersey_number));
        const nextOverrides = { ...copyOverrides };
        detail.conflicts.forEach((c) => {
          if (!(c.player_id in nextOverrides) || taken.has(nextOverrides[c.player_id])) {
            let n = c.jersey_number + 1;
            while (taken.has(n)) n += 1;
            nextOverrides[c.player_id] = n;
          }
          taken.add(nextOverrides[c.player_id]);
        });
        setCopyConflicts(detail.conflicts);
        setCopyOverrides(nextOverrides);
        setCopyError("Some jersey numbers are already taken on this team — review the suggested numbers below, then confirm.");
      } else {
        setCopyError(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setCopySaving(false);
    }
  };

  const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 text-white px-3 py-2 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150";

  const openAddPlayer = () => {
    setPlayerForm(emptyPlayerForm);
    setEditingPlayerId(null);
    setPlayerError("");
    setPlayerDialogOpen(true);
  };

  const openEditPlayer = (p) => {
    setPlayerForm({
      first_name: p.first_name,
      last_name: p.last_name,
      jersey_number: String(p.jersey_number),
      position: p.position || "",
      active: p.active,
    });
    setEditingPlayerId(p.id);
    setPlayerError("");
    setPlayerDialogOpen(true);
  };

  const savePlayer = async (e) => {
    e.preventDefault();
    setPlayerError("");
    setPlayerSaving(true);
    try {
      const payload = {
        first_name: playerForm.first_name.trim(),
        last_name: playerForm.last_name.trim(),
        jersey_number: parseInt(playerForm.jersey_number, 10),
        position: playerForm.position,
        active: playerForm.active,
      };
      if (Number.isNaN(payload.jersey_number)) {
        setPlayerError("Jersey number must be a number.");
        setPlayerSaving(false);
        return;
      }
      if (editingPlayerId) {
        await api.put(`/teams/${currentTeamId}/players/${editingPlayerId}`, payload);
        toast.success("Player updated");
      } else {
        await api.post(`/teams/${currentTeamId}/players`, payload);
        toast.success("Player added");
      }
      setPlayerDialogOpen(false);
      load();
    } catch (err) {
      setPlayerError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPlayerSaving(false);
    }
  };

  const togglePlayerActive = async (p) => {
    try {
      await api.put(`/teams/${currentTeamId}/players/${p.id}`, {
        first_name: p.first_name,
        last_name: p.last_name,
        jersey_number: p.jersey_number,
        position: p.position || "",
        active: !p.active,
      });
      toast.success(!p.active ? "Marked active" : "Marked inactive");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const confirmDeletePlayer = async () => {
    if (!deletePlayerTarget) return;
    try {
      await api.delete(`/teams/${currentTeamId}/players/${deletePlayerTarget.id}`);
      toast.success("Player removed");
      setDeletePlayerTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDeletePlayerTarget(null);
    }
  };

  if (teamsLoading || loading || !isOwner) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <SoccerBall size={40} className="text-[#CCFF00] animate-spin" weight="fill" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="font-label text-xs text-[#CCFF00] mb-1">{currentTeam?.name}</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none">Admin</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            <span className="text-[#CCFF00]">Admin</span> has full control, same as you. <span className="text-blue-400">Member</span> can log events &amp; download clips. <span className="text-zinc-300">Viewer</span> is read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "roster" && adminTierOtherTeams.length > 0 && (
            <button
              onClick={openCopyRoster}
              data-testid="copy-roster-btn"
              className="flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:text-[#CCFF00] hover:border-[#CCFF00] font-label text-xs px-4 py-3 rounded-sm transition-colors duration-150"
            >
              <Copy size={16} weight="bold" /> Copy roster
            </button>
          )}
          {tab === "members" && (
            <button
              onClick={openGrant}
              data-testid="grant-access-btn"
              className="flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:text-[#CCFF00] hover:border-[#CCFF00] font-label text-xs px-4 py-3 rounded-sm transition-colors duration-150"
            >
              <UserPlus size={16} weight="bold" /> Grant access
            </button>
          )}
          {tab !== "audit" && (
            <button
              onClick={tab === "roster" ? openAddPlayer : openAdd}
              data-testid={tab === "roster" ? "add-player-btn" : "add-user-btn"}
              className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-4 py-3 rounded-sm hover:bg-[#B3E600] hover:-translate-y-0.5 transition-all duration-150"
            >
              <Plus size={16} weight="bold" /> {tab === "roster" ? "Add player" : "Add user"}
            </button>
          )}
        </div>
      </motion.div>

      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setTab("roster")}
          data-testid="admin-tab-roster"
          className={`flex items-center gap-2 font-label text-xs uppercase px-4 py-2 rounded-sm transition-colors duration-150 ${tab === "roster" ? "bg-[#CCFF00] text-black" : "bg-[#121212] border border-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          <SoccerBall size={14} weight="bold" /> Roster
        </button>
        <button
          onClick={() => setTab("members")}
          data-testid="admin-tab-members"
          className={`flex items-center gap-2 font-label text-xs uppercase px-4 py-2 rounded-sm transition-colors duration-150 ${tab === "members" ? "bg-[#CCFF00] text-black" : "bg-[#121212] border border-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          <UsersThree size={14} weight="bold" /> Users
        </button>
        <button
          onClick={() => setTab("audit")}
          data-testid="admin-tab-audit"
          className={`flex items-center gap-2 font-label text-xs uppercase px-4 py-2 rounded-sm transition-colors duration-150 ${tab === "audit" ? "bg-[#CCFF00] text-black" : "bg-[#121212] border border-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          <ClockCounterClockwise size={14} weight="bold" /> Audit log
        </button>
      </div>

      {tab === "roster" ? (
        <RosterTable players={players} onTogglePlayerActive={togglePlayerActive} onEditPlayer={openEditPlayer} onDeletePlayer={setDeletePlayerTarget} />
      ) : tab === "members" ? (
        <MembersTable members={members} user={user} onToggleActive={toggleActive} onChangeRole={changeRole} onOpenReset={openReset} onDeleteMember={setDeleteTarget} />
      ) : (
        <AuditLogList auditLog={auditLog} formatDate={formatDate} />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Add user</DialogTitle>
            <DialogDescription className="text-zinc-500">Create a username/password login for a co-admin, an event-logging member, or a read-only viewer.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-user-name" placeholder="Assistant Coach" className={inputCls} />
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Username</label>
              <input required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="add-user-username" placeholder="No email needed — pick anything" className={inputCls} />
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Password</label>
              <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-user-password" placeholder="At least 6 characters" className={inputCls} />
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Role</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, role: "admin" })} data-testid="add-user-role-admin" className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs transition-colors duration-150 ${form.role === "admin" ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400"}`}>
                  <Crown size={14} /> Admin
                </button>
                <button type="button" onClick={() => setForm({ ...form, role: "member" })} data-testid="add-user-role-member" className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs transition-colors duration-150 ${form.role === "member" ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400"}`}>
                  <PencilSimple size={14} /> Member
                </button>
                <button type="button" onClick={() => setForm({ ...form, role: "viewer" })} data-testid="add-user-role-viewer" className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs transition-colors duration-150 ${form.role === "viewer" ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400"}`}>
                  <Eye size={14} /> Viewer
                </button>
              </div>
              <p className="text-xs text-zinc-600 mt-2">
                {form.role === "admin"
                  ? "Full control — same permissions as you, including managing games, roster, video and other users."
                  : form.role === "member"
                    ? "Can log goals/assists/shots/saves and download clips. Can't edit games, roster or other users."
                    : "Read-only — can view roster, games, stats and download clips."}
              </p>
            </div>
            {error && <p className="text-[#FF3B30] text-sm" data-testid="add-user-error">{error}</p>}
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)} className="font-label text-xs px-4 py-2 text-zinc-400 hover:text-white">Cancel</button>
              <button type="submit" disabled={saving} data-testid="save-user-btn" className="bg-[#CCFF00] text-black font-label text-xs px-5 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
                {saving ? "Adding…" : "Add user"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Reset password</DialogTitle>
            <DialogDescription className="text-zinc-500">Set a new password for {resetTarget?.name}. They'll be signed out everywhere immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input type="password" minLength={6} value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} data-testid="reset-password-input" placeholder="New password (min. 6 characters)" className={inputCls} />
            {resetError && <p className="text-[#FF3B30] text-sm" data-testid="reset-password-error">{resetError}</p>}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setResetTarget(null)} className="font-label text-xs px-4 py-2 text-zinc-400 hover:text-white">Cancel</button>
            <button type="button" onClick={confirmReset} disabled={resetting} data-testid="confirm-reset-password-btn" className="bg-[#CCFF00] text-black font-label text-xs px-5 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
              {resetting ? "Resetting…" : "Reset password"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#121212] border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Revoke access?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This removes {deleteTarget?.name}'s access to {currentTeam?.name} only. If they have access to another team, that's unaffected — and their login stays active even if this was their only team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-700 text-white hover:bg-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-remove-user" className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80">Revoke access</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PlayerDialog
        open={playerDialogOpen} onOpenChange={setPlayerDialogOpen} editingPlayerId={editingPlayerId}
        playerForm={playerForm} setPlayerForm={setPlayerForm} playerError={playerError} playerSaving={playerSaving}
        inputCls={inputCls} onSave={savePlayer} onCancel={() => setPlayerDialogOpen(false)}
      />

      <AlertDialog open={!!deletePlayerTarget} onOpenChange={(o) => !o && setDeletePlayerTarget(null)}>
        <AlertDialogContent className="bg-[#121212] border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Remove player?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This permanently removes {deletePlayerTarget?.first_name} {deletePlayerTarget?.last_name} from your roster.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-700 text-white hover:bg-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePlayer} data-testid="confirm-delete-player" className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CopyRosterDialog
        open={copyOpen} onOpenChange={setCopyOpen} currentTeamName={currentTeam?.name} adminTierOtherTeams={adminTierOtherTeams}
        copySourceTeamId={copySourceTeamId} onLoadSourcePlayers={loadSourcePlayers} copyLoading={copyLoading}
        copySourcePlayers={copySourcePlayers} copySelected={copySelected} onToggleSelectAllCopy={toggleSelectAllCopy}
        onToggleCopySelect={toggleCopySelect} copyConflicts={copyConflicts} copyOverrides={copyOverrides}
        setCopyOverrides={setCopyOverrides} copyError={copyError} copySaving={copySaving} onSubmit={submitCopyRoster}
        inputCls={inputCls}
      />

      <GrantAccessDialog
        open={grantOpen} onOpenChange={setGrantOpen} currentTeamName={currentTeam?.name} grantQuery={grantQuery}
        setGrantQuery={setGrantQuery} setGrantSelected={setGrantSelected} grantSearching={grantSearching}
        grantResults={grantResults} grantSelected={grantSelected} grantRole={grantRole} setGrantRole={setGrantRole}
        grantError={grantError} grantSaving={grantSaving} onSubmit={submitGrant} inputCls={inputCls}
      />

    </Layout>
  );
}
